import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { type SupportedLanguage, i18nLocaleToGraphql } from '@modernloop/i18n';
import { useSnackbar } from 'notistack';

import { type Locale, useEmployeeLocaleUpdateMutation } from 'src/generated/mloop-graphql';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EmployeeLocaleUpdateMutation = gql`
  mutation EmployeeLocaleUpdate($input: EmployeeUpdateInput!) {
    employeeUpdate(input: $input) {
      employee {
        id
        locale
      }
    }
  }
`;

/**
 * Returns a callback that persists the employee's locale to the backend.
 * Designed to be passed as `onLanguageChange` to a language selector.
 */
export function useUpdateEmployeeLocale(employeeId: string | undefined) {
  const [mutate] = useEmployeeLocaleUpdateMutation();
  const { enqueueSnackbar } = useSnackbar();

  return useCallback(
    (language: SupportedLanguage) => {
      const graphqlLocale = i18nLocaleToGraphql(language);
      if (!employeeId || !graphqlLocale) {
        return;
      }

      mutate({
        variables: {
          input: {
            id: employeeId,
            set: { locale: graphqlLocale as Locale },
          },
        },
      }).catch(() => {
        enqueueSnackbar('Failed to save language preference', { variant: 'error' });
      });
    },
    [employeeId, mutate, enqueueSnackbar]
  );
}
